import React from 'react';
import { Box } from '@mui/material';
import analytics from '../../utils/analytics';
import PlrModal from '../../feature/preloan/v3-redesign/landing/PlrModal';
import { PlrButton, PlrNotice, PlrTextLink } from '../../feature/preloan/v3-redesign/landing/ui';
import { plr } from '../../feature/preloan/v3-redesign/landing/tokens';

type AlternativeActions = {
  onUnemployedClick: () => Promise<void>,
  onManualEvaluationClick: () => Promise<void>,
};

const EXTERNAL_INFO_ERROR = 'No pudimos procesar tu evaluación porque uno de nuestros proveedores de información está temporalmente fuera de servicio. Vuelve a intentarlo más tarde.';

// Las dos salidas cuando la persona no puede verificarse (mismos eventos y test ids que
// CannotCompleteVerificationDialog): evaluación manual o "estoy desempleado".
export function AlternativeEvaluationOptions({
  onUnemployedClick, onManualEvaluationClick, disabled = false,
}: AlternativeActions & { disabled?: boolean }) {
  const [loading, setLoading] = React.useState<'manual' | 'unemployed' | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const run = async (which: 'manual' | 'unemployed', text: string, fn: () => Promise<void>) => {
    analytics.track('Click Boton', { text, initiator: 'usuario' });
    setError(null);
    setLoading(which);
    try {
      await fn();
    } catch (e) {
      if (e === 509) {
        analytics.track('ERROR DE FUENTE INFORMACION EXTERNA', { initiator: 'backend', errorCode: e });
        setError(EXTERNAL_INFO_ERROR);
      } else {
        analytics.track('ERROR', { initiator: 'backend', errorCode: e });
        setError('Ocurrió un error. Vuelve a intentarlo o contáctanos.');
      }
    }
    setLoading(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <PlrButton
        variant="outlined"
        testId="cu-2fa-manual-evaluation-button"
        loading={loading === 'manual'}
        disabled={disabled || loading !== null}
        onClick={() => run('manual', 'Prefiero que me evalúen manualmente', onManualEvaluationClick)}
        sx={{ width: '100%' }}
      >
        Prefiero una evaluación manual
      </PlrButton>
      <PlrButton
        variant="outlined"
        testId="cu-2fa-unemployed-button"
        loading={loading === 'unemployed'}
        disabled={disabled || loading !== null}
        onClick={() => run('unemployed', 'Estoy desempleado', onUnemployedClick)}
        sx={{ width: '100%' }}
      >
        Estoy desempleado
      </PlrButton>
      {error && <PlrNotice tone="error">{error}</PlrNotice>}
    </Box>
  );
}

AlternativeEvaluationOptions.defaultProps = { disabled: false };

// Link de texto que abre una hoja con las salidas alternativas.
export default function AlternativeEvaluationLink({
  label, title, pageCall, testId, ...actions
}: AlternativeActions & {
  label: string,
  title: string,
  pageCall: { category: string, name: string },
  testId?: string,
}) {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => {
    analytics.page(pageCall.category, pageCall.name);
    setOpen(true);
  };
  return (
    <>
      <PlrTextLink onClick={handleOpen} testId={testId || 'cu-2fa-can-not-complete-button'}>
        {label}
      </PlrTextLink>
      <PlrModal
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        titleId="plr-alternative-evaluation-title"
        testId="plr-alternative-evaluation"
      >
        <Box
          component="p"
          sx={{
            m: '-6px 0 2px', fontSize: { xs: 14, sm: 15 }, lineHeight: 1.45, color: plr.body,
          }}
        >
          Si no puedes completar la verificación, puedes seguir con una de estas opciones:
        </Box>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <AlternativeEvaluationOptions {...actions} />
      </PlrModal>
    </>
  );
}

AlternativeEvaluationLink.defaultProps = { testId: undefined };
